import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, DollarSign, Trash2, SlidersHorizontal, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  getModelID,
  getProcessModelTitle,
  getSourceSystem,
  getTaskArray,
  getVariantArrayUnfiltered,
  getEventLogArray,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { upsertFinding } from '@process-advisor/process-miner/consultant/consultingFindings';

const C = {
  text: '#172B4D',
  body: '#42526E',
  muted: '#6B778C',
  border: '#DFE1E6',
  bg: '#F4F5F7',
  card: '#FFFFFF',
  primary: '#0C66E4',
  primaryTint: '#E9F2FF',
  green: '#00875A',
  orange: '#FF8B00',
  red: '#DE350B',
};

const SCENARIOS = [
  {
    id: 'reassignment_churn',
    label: 'Reduce reassignment / handoff churn',
    description: 'Models operational effort saved by reducing unnecessary ownership or group handoffs.',
  },
  {
    id: 'rework_loops',
    label: 'Reduce rework / loopbacks',
    description: 'Models effort saved by reducing reopened, recycled or high-transition work items.',
  },
  {
    id: 'waiting_delay',
    label: 'Reduce waiting / delay time',
    description: 'Models value from reducing elapsed delay. Uses a conservative attribution factor unless changed.',
  },
  {
    id: 'approval_delay',
    label: 'Reduce approval delay',
    description: 'Models value from reducing approval or authorization dwell. Uses detected approval signals where available.',
  },
  {
    id: 'variant_standardisation',
    label: 'Standardise long-tail variants',
    description: 'Models effort saved by reducing low-value process variation outside the most common variants.',
  },
  {
    id: 'top_bottleneck_activity',
    label: 'Reduce top bottleneck activity time',
    description: 'Models value from reducing dwell time in the activity/status consuming the most elapsed time.',
  },
  {
    id: 'post_resolution_admin',
    label: 'Reduce post-resolution admin delay',
    description: 'Models value from reducing administrative delay after work is effectively resolved or completed.',
  },
  {
    id: 'best_path_shift',
    label: 'Shift work to best-performing path',
    description: 'Models value from moving slow or inconsistent work towards the best-performing common process path.',
  },
  {
    id: 'automate_repetitive_transition',
    label: 'Automate repetitive transition',
    description: 'Models direct effort saved by automating a high-volume recurring transition.',
  },
];


const CURRENCIES = [
  { code: 'AUD', label: 'AUD — Australian Dollar', symbol: '$' },
  { code: 'USD', label: 'USD — US Dollar', symbol: '$' },
  { code: 'EUR', label: 'EUR — Euro', symbol: '€' },
  { code: 'GBP', label: 'GBP — British Pound', symbol: '£' },
  { code: 'CAD', label: 'CAD — Canadian Dollar', symbol: '$' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar', symbol: '$' },
  { code: 'SGD', label: 'SGD — Singapore Dollar', symbol: '$' },
  { code: 'JPY', label: 'JPY — Japanese Yen', symbol: '¥' },
  { code: 'CHF', label: 'CHF — Swiss Franc', symbol: 'CHF' },
  { code: 'INR', label: 'INR — Indian Rupee', symbol: '₹' },
  { code: 'ZAR', label: 'ZAR — South African Rand', symbol: 'R' },
];

const VALUE_MODES = {
  conservative: { label: 'Conservative', factor: 0.05, help: 'Counts only a small portion of elapsed delay as operational value.' },
  balanced: { label: 'Balanced', factor: 0.15, help: 'Counts a modest portion of elapsed delay as operational value.' },
  aggressive: { label: 'Aggressive', factor: 0.35, help: 'Counts a larger portion of delay reduction as business value.' },
};

function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function visibleTasks() {
  return (getTaskArray ? getTaskArray() : []).filter(t => t && (t.filtered === 0 || t.filtered === undefined));
}

function taskTransitions(task) {
  const explicit = Number(task?.transitions);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const si = Number(task?.si);
  const fi = Number(task?.fi);
  if (Number.isFinite(si) && Number.isFinite(fi) && fi >= si) return Math.max(0, fi - si + 1);
  return 0;
}

function taskDurationSeconds(task) {
  return asNumber(task?.d || task?.Duration || task?.duration_seconds || task?.lead_time_seconds || 0, 0);
}

function cleanTextValue(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return '(blank)';
  return raw.replaceAll('^^', ', ');
}

function eventTaskId(event) {
  return String(event?.task || event?.task_id || event?.case || event?.case_id || event?.item || event?.id || '').trim();
}

function eventActivity(event) {
  return String(event?.activity || event?.status || event?.state || event?.activity_name || event?.a || '').trim();
}

function eventTimestampMs(event) {
  const raw = event?.timestamp || event?.time || event?.date || event?.ts || event?.t;
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function isResolutionActivity(value) {
  const v = String(value || '').toLowerCase();
  return /resolved|resolution|solved|complete|completed|done|closed/.test(v);
}

function getActivityDwellSummary(events) {
  const byTask = new Map();
  (events || []).forEach(event => {
    const taskId = eventTaskId(event);
    const ts = eventTimestampMs(event);
    if (!taskId || ts == null) return;
    if (!byTask.has(taskId)) byTask.set(taskId, []);
    byTask.get(taskId).push({ ...event, __ts: ts, __activity: cleanTextValue(eventActivity(event)) });
  });

  const activitySeconds = new Map();
  const transitionCounts = new Map();
  let postResolutionSeconds = 0;

  byTask.forEach(list => {
    const sorted = list.sort((a, b) => a.__ts - b.__ts);
    let firstResolutionTs = null;
    for (let i = 0; i < sorted.length; i += 1) {
      const current = sorted[i];
      const next = sorted[i + 1];
      if (isResolutionActivity(current.__activity) && firstResolutionTs == null) firstResolutionTs = current.__ts;
      if (next) {
        const dwell = Math.max(0, (next.__ts - current.__ts) / 1000);
        if (dwell > 0 && current.__activity) {
          activitySeconds.set(current.__activity, (activitySeconds.get(current.__activity) || 0) + dwell);
        }
        const pair = `${current.__activity} → ${next.__activity}`;
        transitionCounts.set(pair, (transitionCounts.get(pair) || 0) + 1);
      }
    }
    if (firstResolutionTs != null && sorted.length > 0) {
      const finalTs = sorted[sorted.length - 1].__ts;
      if (finalTs > firstResolutionTs) postResolutionSeconds += (finalTs - firstResolutionTs) / 1000;
    }
  });

  const topActivity = [...activitySeconds.entries()].sort((a, b) => b[1] - a[1])[0] || null;
  const topTransition = [...transitionCounts.entries()].sort((a, b) => b[1] - a[1])[0] || null;

  return {
    topActivity: topActivity ? { name: topActivity[0], seconds: topActivity[1] } : null,
    topTransition: topTransition ? { name: topTransition[0], count: topTransition[1] } : null,
    postResolutionSeconds,
  };
}

function getBestPathSummary(variants, tasks, averageDuration) {
  const rows = [...(variants || [])]
    .map(v => ({
      id: String(v?.id ?? v?.vid ?? ''),
      tasks: asNumber(v?.tasks || v?.count || v?.cases || 0, 0),
      duration: asNumber(v?.duration || v?.avgDuration || v?.avg_duration || v?.averageDuration || v?.d || 0, 0),
    }))
    .filter(v => v.tasks > 0);

  if (!rows.length) {
    return { bestDuration: averageDuration || 0, slowPathTasks: Math.max(0, tasks.length), potentialSeconds: Math.max(0, (averageDuration || 0) * tasks.length * 0.25), method: 'fallback' };
  }

  const best = [...rows].filter(v => v.duration > 0).sort((a, b) => a.duration - b.duration)[0];
  const bestDuration = best?.duration || averageDuration || 0;
  let slowPathTasks = 0;
  let potentialSeconds = 0;
  rows.forEach(v => {
    if (v.duration && bestDuration && v.duration > bestDuration * 1.25) {
      slowPathTasks += v.tasks;
      potentialSeconds += (v.duration - bestDuration) * v.tasks;
    }
  });
  return { bestDuration, slowPathTasks, potentialSeconds, method: best ? 'variant_duration' : 'fallback' };
}

function fmtNumber(n, digits = 0) {
  const value = Number(n || 0);
  return value.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function fmtMoney(n, currency = 'AUD') {
  const value = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch (_) {
    return `$${fmtNumber(value)}`;
  }
}

function fmtHours(n) {
  const value = Number(n || 0);
  if (value < 1) return `${Math.round(value * 60)} min`;
  return `${fmtNumber(value, value < 10 ? 1 : 0)} hrs`;
}

function storageKey() {
  const id = getModelID ? getModelID() : 'unsaved';
  return `metricus.valueSimulation.${id || 'unsaved'}`;
}

function getVariantById(variants) {
  const map = new Map();
  (variants || []).forEach(v => {
    if (v) map.set(String(v.id ?? v.vid ?? ''), v);
  });
  return map;
}

function buildBaseline() {
  const tasks = visibleTasks();
  const variants = getVariantArrayUnfiltered ? getVariantArrayUnfiltered() : [];
  const events = getEventLogArray ? getEventLogArray() : [];
  const variantMap = getVariantById(variants);
  const totalTransitions = tasks.reduce((sum, t) => sum + taskTransitions(t), 0);
  const excessTransitions = tasks.reduce((sum, t) => sum + Math.max(0, taskTransitions(t) - 1), 0);
  const totalDuration = tasks.reduce((sum, t) => sum + taskDurationSeconds(t), 0);
  const reworkTasks = tasks.reduce((sum, t) => {
    const v = variantMap.get(String(t?.vid ?? ''));
    const transitions = taskTransitions(t);
    const loops = asNumber(v?.loops || v?.loop_count || 0, 0);
    return sum + ((loops > 0 || transitions > 5) ? 1 : 0);
  }, 0);
  const approvalSignalTasks = tasks.reduce((sum, t) => {
    const text = `${t?.approval || ''} ${t?.state || ''} ${t?.status || ''}`.toLowerCase();
    return sum + (text.includes('approv') || text.includes('authoriz') || text.includes('authoris') ? 1 : 0);
  }, 0);
  const variantsSorted = [...(variants || [])].sort((a, b) => asNumber(b.tasks || b.count || 0) - asNumber(a.tasks || a.count || 0));
  const topFiveTasks = variantsSorted.slice(0, 5).reduce((sum, v) => sum + asNumber(v.tasks || v.count || 0), 0);
  const longTailTasks = Math.max(0, tasks.length - topFiveTasks);
  const dwellSummary = getActivityDwellSummary(events);
  const adminTimeSeconds = tasks.reduce((sum, t) => sum + asNumber(t?.admin_time_seconds || 0, 0), 0);
  const postResolutionSeconds = adminTimeSeconds > 0 ? adminTimeSeconds : dwellSummary.postResolutionSeconds;
  const bestPathSummary = getBestPathSummary(variants, tasks, tasks.length ? totalDuration / tasks.length : 0);
  return {
    tasks,
    variants,
    events,
    totalTasks: tasks.length,
    totalTransitions,
    excessTransitions,
    totalDuration,
    avgDuration: tasks.length ? totalDuration / tasks.length : 0,
    reworkTasks,
    approvalSignalTasks,
    longTailTasks,
    variantCount: variants.length,
    topBottleneckActivity: dwellSummary.topActivity,
    topTransition: dwellSummary.topTransition,
    postResolutionSeconds,
    bestPathSummary,
  };
}

function calculateScenario({ scenarioId, targetReduction, hourlyCost, currency, valueMode, minutesPerHandoff, minutesPerRework, minutesPerVariant, minutesPerAutomatedTransition }) {
  const baseline = buildBaseline();
  const reduction = Math.max(0, Math.min(100, asNumber(targetReduction, 0))) / 100;
  const cost = Math.max(0, asNumber(hourlyCost, 0));
  const mode = VALUE_MODES[valueMode] || VALUE_MODES.balanced;
  let basis = '';
  let affected = 0;
  let hours = 0;
  let confidence = 'Medium';
  let evidence = [];
  let notes = [];

  if (scenarioId === 'reassignment_churn') {
    affected = Math.round(baseline.excessTransitions * reduction);
    hours = affected * Math.max(1, asNumber(minutesPerHandoff, 15)) / 60;
    basis = 'Avoided handoff effort';
    confidence = baseline.excessTransitions > 0 ? 'Medium' : 'Low';
    evidence = [
      `${fmtNumber(baseline.totalTransitions)} total transitions detected`,
      `${fmtNumber(baseline.excessTransitions)} transitions beyond first-touch baseline`,
      `${Math.round(reduction * 100)}% improvement target`,
    ];
  } else if (scenarioId === 'rework_loops') {
    affected = Math.round(baseline.reworkTasks * reduction);
    hours = affected * Math.max(1, asNumber(minutesPerRework, 45)) / 60;
    basis = 'Avoided rework effort';
    confidence = baseline.reworkTasks > 0 ? 'Medium' : 'Low';
    evidence = [
      `${fmtNumber(baseline.reworkTasks)} work items show a rework / loop signal`,
      `${Math.round(reduction * 100)}% improvement target`,
      `${fmtNumber(minutesPerRework)} minutes assumed per avoided rework item`,
    ];
  } else if (scenarioId === 'waiting_delay') {
    const reducedDelayHours = (baseline.totalDuration / 3600) * reduction;
    hours = reducedDelayHours * mode.factor;
    affected = baseline.totalTasks;
    basis = `${mode.label} delay-value attribution`;
    confidence = baseline.totalDuration > 0 ? 'Directional' : 'Low';
    evidence = [
      `${fmtHours(baseline.totalDuration / 3600)} total elapsed duration in loaded work items`,
      `${Math.round(reduction * 100)}% delay reduction target`,
      `${Math.round(mode.factor * 100)}% of reduced delay counted as operational value`,
    ];
    notes.push('Elapsed delay is not the same as human effort. The attribution mode controls how much delay reduction is converted into value.');
  } else if (scenarioId === 'approval_delay') {
    const baseHours = baseline.approvalSignalTasks > 0
      ? (baseline.avgDuration / 3600) * baseline.approvalSignalTasks * 0.25
      : (baseline.totalDuration / 3600) * 0.08;
    const reducedDelayHours = baseHours * reduction;
    hours = reducedDelayHours * mode.factor;
    affected = baseline.approvalSignalTasks || Math.round(baseline.totalTasks * 0.1);
    basis = 'Approval dwell estimate';
    confidence = baseline.approvalSignalTasks > 0 ? 'Directional' : 'Low';
    evidence = [
      baseline.approvalSignalTasks > 0 ? `${fmtNumber(baseline.approvalSignalTasks)} work items have approval / authorization signals` : 'No explicit approval field signal detected; using conservative fallback',
      `${Math.round(reduction * 100)}% improvement target`,
      `${Math.round(mode.factor * 100)}% of reduced approval delay counted as operational value`,
    ];
  } else if (scenarioId === 'variant_standardisation') {
    affected = Math.round(baseline.longTailTasks * reduction);
    hours = affected * Math.max(1, asNumber(minutesPerVariant, 20)) / 60;
    basis = 'Long-tail variant simplification';
    confidence = baseline.variantCount > 0 ? 'Medium' : 'Low';
    evidence = [
      `${fmtNumber(baseline.variantCount)} variants detected`,
      `${fmtNumber(baseline.longTailTasks)} work items outside the top 5 variants`,
      `${fmtNumber(minutesPerVariant)} minutes assumed per simplified long-tail item`,
    ];
  } else if (scenarioId === 'top_bottleneck_activity') {
    const top = baseline.topBottleneckActivity;
    const bottleneckHours = asNumber(top?.seconds || 0, 0) / 3600;
    const reducedDelayHours = bottleneckHours * reduction;
    hours = reducedDelayHours * mode.factor;
    affected = baseline.totalTasks;
    basis = top?.name ? `Top bottleneck: ${top.name}` : 'Top bottleneck estimate';
    confidence = top?.seconds > 0 ? 'Directional' : 'Low';
    evidence = [
      top?.name ? `${top.name} has ${fmtHours(bottleneckHours)} estimated dwell time` : 'No event-level bottleneck dwell detected; using fallback estimate',
      `${Math.round(reduction * 100)}% bottleneck reduction target`,
      `${Math.round(mode.factor * 100)}% of reduced bottleneck time counted as operational value`,
    ];
    notes.push('This scenario depends on event timestamps and treats bottleneck reduction as elapsed-time value, not direct effort saved.');
  } else if (scenarioId === 'post_resolution_admin') {
    const adminHours = asNumber(baseline.postResolutionSeconds || 0, 0) / 3600;
    const reducedDelayHours = adminHours * reduction;
    hours = reducedDelayHours * mode.factor;
    affected = baseline.totalTasks;
    basis = 'Post-resolution admin delay';
    confidence = adminHours > 0 ? 'Directional' : 'Low';
    evidence = [
      adminHours > 0 ? `${fmtHours(adminHours)} post-resolution / admin delay detected` : 'No explicit post-resolution admin delay detected; result may be understated',
      `${Math.round(reduction * 100)}% admin delay reduction target`,
      `${Math.round(mode.factor * 100)}% of reduced admin delay counted as operational value`,
    ];
    notes.push('Best used when the process has clear resolved/completed/closed activity signals or admin-time metrics.');
  } else if (scenarioId === 'best_path_shift') {
    const bestPath = baseline.bestPathSummary || {};
    const potentialHours = asNumber(bestPath.potentialSeconds || 0, 0) / 3600;
    const reducedDelayHours = potentialHours * reduction;
    hours = reducedDelayHours * mode.factor;
    affected = Math.round(asNumber(bestPath.slowPathTasks || baseline.longTailTasks || baseline.totalTasks, 0) * reduction);
    basis = 'Best-performing path shift';
    confidence = bestPath.method === 'variant_duration' ? 'Directional' : 'Low';
    evidence = [
      `${fmtNumber(baseline.variantCount)} variants detected`,
      `${fmtNumber(bestPath.slowPathTasks || baseline.longTailTasks || 0)} work items identified as candidates for path standardisation`,
      `${Math.round(mode.factor * 100)}% of reduced path delay counted as operational value`,
    ];
    notes.push('This scenario estimates the value of moving work from slower paths towards a better-performing common path.');
  } else if (scenarioId === 'automate_repetitive_transition') {
    const topTransition = baseline.topTransition;
    const transitionCount = asNumber(topTransition?.count || baseline.excessTransitions || 0, 0);
    affected = Math.round(transitionCount * reduction);
    hours = affected * Math.max(1, asNumber(minutesPerAutomatedTransition, 5)) / 60;
    basis = topTransition?.name ? `Automate: ${topTransition.name}` : 'Automated repetitive transition';
    confidence = transitionCount > 0 ? 'Medium' : 'Low';
    evidence = [
      topTransition?.name ? `${topTransition.name} occurs ${fmtNumber(transitionCount)} times` : `${fmtNumber(transitionCount)} candidate repetitive transitions detected`,
      `${Math.round(reduction * 100)}% automation coverage target`,
      `${fmtNumber(minutesPerAutomatedTransition)} minutes assumed per automated transition`,
    ];
  }

  const analysisSaving = hours * cost;
  const annualSaving = analysisSaving * 12;
  return {
    scenarioId,
    scenarioLabel: SCENARIOS.find(s => s.id === scenarioId)?.label || 'Value scenario',
    targetReduction: Math.round(reduction * 100),
    affected,
    hours,
    analysisSaving,
    annualSaving,
    currency,
    confidence,
    basis,
    evidence,
    notes,
    baseline,
  };
}



function buildFindingScenarioLink(record) {
  const r = record?.result || {};
  return {
    id: record?.id || `scenario_${Date.now()}`,
    scenarioId: r.scenarioId || '',
    scenarioLabel: r.scenarioLabel || 'Value scenario',
    createdAt: record?.createdAt || new Date().toISOString(),
    assumptions: record?.assumptions || {},
    targetReduction: r.targetReduction || 0,
    hours: r.hours || 0,
    analysisSaving: r.analysisSaving || 0,
    annualSaving: r.annualSaving || 0,
    currency: r.currency || 'AUD',
    confidence: r.confidence || 'Medium',
    basis: r.basis || '',
    evidence: Array.isArray(r.evidence) ? r.evidence : [],
  };
}

function getValueFindingText(result) {
  const scenarioId = result?.scenarioId || '';
  const scenarioLabel = result?.scenarioLabel || 'Value scenario';
  const valueText = `${fmtMoney(result?.annualSaving || 0, result?.currency || 'AUD')} annualised value estimate`;

  const defaults = {
    title: `${scenarioLabel} value opportunity`,
    finding: `${scenarioLabel} represents a potential process improvement opportunity with an estimated ${valueText}.`,
    recommendation: 'Review the underlying process evidence and validate whether the modelled improvement target is operationally realistic.',
    action: 'Add this opportunity to the assessment for validation, ownership, and implementation planning.',
  };

  const byScenario = {
    reassignment_churn: {
      title: 'Reassignment churn is creating avoidable operational effort',
      finding: `Unnecessary handoffs or ownership changes appear to be creating avoidable effort and delay. The current simulation estimates ${valueText}.`,
      recommendation: 'Review assignment rules, triage logic, resolver ownership, and routing paths for common work types.',
      action: 'Validate the highest-volume handoff paths and identify routing or ownership changes that could reduce avoidable reassignment.',
    },
    rework_loops: {
      title: 'Rework and loopbacks are creating avoidable process effort',
      finding: `Rework or loopback patterns appear to be creating avoidable effort. The current simulation estimates ${valueText}.`,
      recommendation: 'Review the recurring loopback patterns, entry criteria, quality checks, and closure or resolution rules.',
      action: 'Validate the largest loopback drivers and define actions to reduce avoidable rework.',
    },
    waiting_delay: {
      title: 'Waiting time reduction could release measurable operational value',
      finding: `Elapsed waiting or delay time appears to be a material source of operational drag. The current simulation estimates ${valueText}.`,
      recommendation: 'Review the longest waiting states, idle periods, and queue ownership points to identify realistic delay-reduction opportunities.',
      action: 'Validate the delay attribution assumptions and prioritise changes that reduce avoidable waiting time.',
    },
    approval_delay: {
      title: 'Approval delay could be a material value opportunity',
      finding: `Approval or authorisation dwell appears to be a potential source of avoidable delay. The current simulation estimates ${valueText}.`,
      recommendation: 'Review approval policies, approval routing, thresholds, and standard/pre-approved paths where appropriate.',
      action: 'Validate approval bottlenecks and identify low-risk approval simplification opportunities.',
    },
    variant_standardisation: {
      title: 'Long-tail process variation could be simplified for value',
      finding: `Long-tail process variants appear to be creating avoidable complexity. The current simulation estimates ${valueText}.`,
      recommendation: 'Review low-volume or high-friction variants and identify where standardisation or automation would reduce avoidable effort.',
      action: 'Prioritise the long-tail variants with the strongest volume, delay, or rework evidence.',
    },
    top_bottleneck_activity: {
      title: 'Top bottleneck activity is creating avoidable delay',
      finding: `The highest-dwell activity or status appears to be a material source of avoidable delay. The current simulation estimates ${valueText}.`,
      recommendation: 'Review the bottleneck activity, ownership model, entry/exit criteria, and routing rules to identify realistic dwell-time reduction opportunities.',
      action: 'Validate the bottleneck in Evidence and Explorer, then define changes that reduce time spent in the slowest activity.',
    },
    post_resolution_admin: {
      title: 'Post-resolution administration is delaying completion',
      finding: `Administrative or closure delay after effective resolution appears to be creating avoidable elapsed time. The current simulation estimates ${valueText}.`,
      recommendation: 'Review closure, resolution, verification, and administrative completion steps to remove unnecessary waiting or manual handling.',
      action: 'Confirm the post-resolution delay pattern and identify automation or policy changes to shorten closure/admin time.',
    },
    best_path_shift: {
      title: 'Moving work to better-performing paths could reduce delay',
      finding: `Slow or inconsistent process paths appear to be creating avoidable delay compared with better-performing common paths. The current simulation estimates ${valueText}.`,
      recommendation: 'Compare high-volume slow paths with faster common paths and identify where process guidance, workflow simplification, or automation can shift work to the better path.',
      action: 'Select candidate variants, validate feasibility with process owners, and define the target standard path.',
    },
    automate_repetitive_transition: {
      title: 'A repetitive transition is a candidate for automation',
      finding: `A high-volume recurring transition appears to be a candidate for automation or workflow rule support. The current simulation estimates ${valueText}.`,
      recommendation: 'Review the repetitive transition for automation suitability, rule conditions, exception handling, and owner approval.',
      action: 'Validate the transition volume and create an automation candidate in the improvement backlog.',
    },
  };

  return byScenario[scenarioId] || defaults;
}

function mapFindingConfidence(value) {
  const v = String(value || '').toLowerCase();
  if (v.includes('low')) return 'Weak';
  if (v.includes('direction')) return 'Moderate';
  return 'Moderate';
}

function valuePotentialFromAnnualSaving(value) {
  const n = Number(value || 0);
  if (n >= 100000) return 'High';
  if (n >= 25000) return 'Medium';
  return 'Low';
}

function severityFromAnnualSaving(value) {
  const n = Number(value || 0);
  if (n >= 150000) return 'High';
  if (n >= 25000) return 'Medium';
  return 'Low';
}

function buildValueFinding({ result, assumptions, record }) {
  const text = getValueFindingText(result);
  const valueEvidence = [
    ...(Array.isArray(result?.evidence) ? result.evidence : []),
    `${fmtHours(result?.hours || 0)} estimated value-equivalent hours saved`,
    `${fmtMoney(result?.analysisSaving || 0, result?.currency || assumptions?.currency || 'AUD')} estimated value for the analysis period`,
    `${fmtMoney(result?.annualSaving || 0, result?.currency || assumptions?.currency || 'AUD')} annualised estimate`,
  ];

  const assumptionText = [
    `Scenario: ${result?.scenarioLabel || 'Value scenario'}`,
    `Improvement target: ${result?.targetReduction || assumptions?.targetReduction || 0}%`,
    `Hourly cost: ${fmtMoney(assumptions?.hourlyCost || 0, assumptions?.currency || result?.currency || 'AUD')}`,
    `Value mode: ${VALUE_MODES[assumptions?.valueMode]?.label || assumptions?.valueMode || 'Balanced'}`,
  ].join('\n');

  return {
    title: text.title,
    area: 'Value Opportunity',
    severity: severityFromAnnualSaving(result?.annualSaving),
    confidence: mapFindingConfidence(result?.confidence),
    valuePotential: valuePotentialFromAnnualSaving(result?.annualSaving),
    effort: 'Medium',
    finding: text.finding,
    evidence: valueEvidence,
    recommendation: text.recommendation,
    action: text.action,
    serviceNowAreas: [],
    sourceTabs: ['Value Simulation'],
    status: 'New',
    consultantNotes: `Created from Value Simulation.
${assumptionText}`,
    valueScenarios: record ? [buildFindingScenarioLink(record)] : [],
  };
}

function Badge({ children, tone = 'blue' }) {
  const colors = {
    blue: ['#E9F2FF', '#0C66E4'],
    green: ['#E3FCEF', '#006644'],
    orange: ['#FFF0B3', '#974F0C'],
    red: ['#FFEBE6', '#BF2600'],
    grey: ['#EBECF0', '#42526E'],
  };
  const [bg, color] = colors[tone] || colors.blue;
  return <span style={{ background: bg, color, borderRadius: 999, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>{children}</span>;
}

export default function ValueSimulation({ processLabel = 'Process', entityLabel = 'Work Item' }) {
  const [scenarioId, setScenarioId] = useState('reassignment_churn');
  const [targetReduction, setTargetReduction] = useState(25);
  const [hourlyCost, setHourlyCost] = useState(120);
  const [currency, setCurrency] = useState('AUD');
  const [valueMode, setValueMode] = useState('balanced');
  const [minutesPerHandoff, setMinutesPerHandoff] = useState(15);
  const [minutesPerRework, setMinutesPerRework] = useState(45);
  const [minutesPerVariant, setMinutesPerVariant] = useState(20);
  const [minutesPerAutomatedTransition, setMinutesPerAutomatedTransition] = useState(5);
  const [saved, setSaved] = useState([]);
  const [savedFindingMessage, setSavedFindingMessage] = useState('');
  const [savedScenarioMessage, setSavedScenarioMessage] = useState('');
  const [tick, setTick] = useState(0);
  const [linkedOpportunity, setLinkedOpportunity] = useState(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey());
      setSaved(raw ? JSON.parse(raw) : []);
    } catch (_) { setSaved([]); }
  }, []);

  useEffect(() => {
    try {
      const modelId = getModelID ? getModelID() : 'unsaved';
      const key = `metricus.pendingValueOpportunity.${modelId || 'unsaved'}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const opportunity = parsed?.opportunity;
      if (!opportunity) return;
      setLinkedOpportunity(opportunity);
      const suggestedScenario = String(opportunity.value_simulation_scenario || '').trim();
      if (SCENARIOS.some((scenario) => scenario.id === suggestedScenario)) setScenarioId(suggestedScenario);
      window.localStorage.removeItem(key);
    } catch (_) {}
  }, []);

  const result = useMemo(() => calculateScenario({
    scenarioId,
    targetReduction,
    hourlyCost,
    currency,
    valueMode,
    minutesPerHandoff,
    minutesPerRework,
    minutesPerVariant,
    minutesPerAutomatedTransition,
  }), [scenarioId, targetReduction, hourlyCost, currency, valueMode, minutesPerHandoff, minutesPerRework, minutesPerVariant, minutesPerAutomatedTransition, tick]);

  const saveScenario = () => {
    const record = {
      id: `scenario_${Date.now()}`,
      modelId: getModelID ? getModelID() : '',
      modelTitle: getProcessModelTitle ? getProcessModelTitle() : '',
      sourceSystem: getSourceSystem ? getSourceSystem() : 'generic',
      createdAt: new Date().toISOString(),
      assumptions: { targetReduction, hourlyCost, currency, valueMode, minutesPerHandoff, minutesPerRework, minutesPerVariant, minutesPerAutomatedTransition },
      result,
      linkedOpportunity: linkedOpportunity ? { title: linkedOpportunity.title, opportunity_type: linkedOpportunity.opportunity_type, value_potential: linkedOpportunity.value_potential, recommended_priority: linkedOpportunity.recommended_priority } : null,
    };
    const next = [record, ...saved].slice(0, 25);
    setSaved(next);
    try { window.localStorage.setItem(storageKey(), JSON.stringify(next)); } catch (_) {}
    setSavedScenarioMessage('Saved value scenario for comparison.');
    window.setTimeout(() => setSavedScenarioMessage(''), 4000);
  };


  const saveFinding = () => {
    const record = {
      id: `scenario_${Date.now()}`,
      modelId: getModelID ? getModelID() : '',
      modelTitle: getProcessModelTitle ? getProcessModelTitle() : '',
      sourceSystem: getSourceSystem ? getSourceSystem() : 'generic',
      createdAt: new Date().toISOString(),
      assumptions: { targetReduction, hourlyCost, currency, valueMode, minutesPerHandoff, minutesPerRework, minutesPerVariant, minutesPerAutomatedTransition },
      result,
      linkedOpportunity: linkedOpportunity ? { title: linkedOpportunity.title, opportunity_type: linkedOpportunity.opportunity_type, value_potential: linkedOpportunity.value_potential, recommended_priority: linkedOpportunity.recommended_priority } : null,
    };
    const finding = buildValueFinding({ result, assumptions: record.assumptions, record });
    upsertFinding?.(finding);
    setSavedFindingMessage('Saved value finding to the Assessment.');
    window.setTimeout(() => setSavedFindingMessage(''), 4000);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onSaveScenario = () => saveScenario();
    const onSaveFinding = () => saveFinding();
    window.addEventListener('metricus:save-value-scenario', onSaveScenario);
    window.addEventListener('metricus:save-value-finding', onSaveFinding);
    return () => {
      window.removeEventListener('metricus:save-value-scenario', onSaveScenario);
      window.removeEventListener('metricus:save-value-finding', onSaveFinding);
    };
  });

  const deleteScenario = (id) => {
    const next = saved.filter(s => s.id !== id);
    setSaved(next);
    try { window.localStorage.setItem(storageKey(), JSON.stringify(next)); } catch (_) {}
    setSavedScenarioMessage('Saved value scenario for comparison.');
    window.setTimeout(() => setSavedScenarioMessage(''), 4000);
  };

  const refresh = () => { setTick(v => v + 1); };
  const scenario = SCENARIOS.find(s => s.id === scenarioId) || SCENARIOS[0];
  const baseline = result.baseline || {};

  const valueSimulationCss = `
    /*
      ServiceNow wrapper CSS applies broad !important rules to h2, label,
      input and select elements. Scope the Value page back to the Forge visual
      scale so ServiceNow and Forge render the same.
    */
    .pa-value-simulation {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif !important;
      font-size: 13px !important;
      line-height: 1.42 !important;
      color: #172B4D !important;
    }

    .pa-value-simulation h2.pa-value-card-title {
      font-size: 16px !important;
      line-height: 20px !important;
      font-weight: 800 !important;
      margin: 0 !important;
      color: #172B4D !important;
    }

    .pa-value-simulation label.pa-value-label {
      display: block !important;
      font-size: 10.5px !important;
      line-height: 14px !important;
      font-weight: 800 !important;
      text-transform: uppercase !important;
      letter-spacing: .055em !important;
      color: #5E6C84 !important;
      margin: 11px 0 5px !important;
    }

    .pa-value-simulation input.pa-value-input,
    .pa-value-simulation select.pa-value-input {
      width: 100% !important;
      box-sizing: border-box !important;
      min-height: 34px !important;
      height: 34px !important;
      border: 1px solid #DFE1E6 !important;
      border-radius: 8px !important;
      padding: 7px 10px !important;
      font-size: 12.5px !important;
      line-height: 18px !important;
      font-weight: 400 !important;
      color: #172B4D !important;
      background: #FFFFFF !important;
      font-family: inherit !important;
      box-shadow: none !important;
      outline: none !important;
    }

    .pa-value-simulation select.pa-value-input {
      padding-right: 28px !important;
    }

    .pa-value-simulation .pa-value-help-text {
      font-size: 12px !important;
      line-height: 1.45 !important;
      color: #5E6C84 !important;
      font-weight: 400 !important;
    }

    .pa-value-simulation .pa-value-note {
      font-size: 12px !important;
      line-height: 1.4 !important;
      font-weight: 400 !important;
    }

    .pa-value-simulation .pa-value-refresh-button {
      font-size: 12px !important;
      line-height: 16px !important;
      font-weight: 800 !important;
    }
  `;

  const styles = {
    page: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif', color: C.text, background: C.bg, minHeight: '100%', padding: 12, fontSize: 13, lineHeight: 1.42 },
    header: { background: 'linear-gradient(135deg, #F0F4FF 0%, #E9F2FF 100%)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, marginBottom: 14 },
    title: { margin: 0, fontSize: 22, fontWeight: 800, color: C.primary },
    sub: { margin: '5px 0 0', fontSize: 13, color: C.body, lineHeight: 1.45, maxWidth: 900 },
    grid: { display: 'grid', gridTemplateColumns: 'minmax(320px, 430px) 1fr', gap: 14, alignItems: 'start' },
    card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, boxShadow: '0 1px 2px rgba(9,30,66,0.06)' },
    cardTitle: { display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 16, fontWeight: 800 },
    label: { display: 'block', fontSize: 10.5, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.055em', margin: '11px 0 5px', lineHeight: '14px' },
    input: { width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px', fontSize: 12.5, lineHeight: '18px', color: C.text, background: C.card, fontFamily: 'inherit' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    metricGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 },
    metric: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 },
    metricLabel: { fontSize: 10.5, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.055em', lineHeight: '14px' },
    metricValue: { marginTop: 4, fontSize: 24, color: C.text, fontWeight: 850 },
    button: { display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${C.primary}`, background: C.primary, color: '#fff', borderRadius: 8, padding: '8px 12px', fontWeight: 800, cursor: 'pointer' },
    secondaryButton: { display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${C.border}`, background: '#fff', color: C.primary, borderRadius: 8, padding: '8px 12px', fontWeight: 800, cursor: 'pointer' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', color: C.muted, background: '#F7F8FA', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', padding: '8px 9px', borderBottom: `1px solid ${C.border}` },
    td: { padding: '9px', borderBottom: '1px solid #EBECF0', color: C.body, verticalAlign: 'top' },
    note: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10, borderRadius: 10, background: '#FFF7D6', border: '1px solid #F5CD47', color: '#5F3811', fontSize: 12, lineHeight: 1.4, marginTop: 12 },
    successNote: { display: 'flex', alignItems: 'center', gap: 8, padding: 10, borderRadius: 10, background: '#E3FCEF', border: '1px solid #ABF5D1', color: '#006644', fontSize: 12, lineHeight: 1.4, marginTop: 12 },
  };

  return (
    <div className="pa-value-simulation" style={styles.page}>
      <style>{valueSimulationCss}</style>
      {linkedOpportunity && (
        <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 10, background: '#F7FAFF', border: '1px solid #B3D4FF', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: '#0C66E4', marginBottom: 4 }}>Opportunity value validation</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>{linkedOpportunity.title}</div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: C.body }}>{linkedOpportunity.value_hypothesis || linkedOpportunity.expected_benefit || 'Validate the value of this opportunity using the process baseline and customer assumptions.'}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {linkedOpportunity.value_potential && <Badge tone="green">Value: {linkedOpportunity.value_potential}</Badge>}
            {linkedOpportunity.recommended_priority && <Badge>{linkedOpportunity.recommended_priority}</Badge>}
            {linkedOpportunity.value_driver && <Badge tone="grey">{linkedOpportunity.value_driver}</Badge>}
          </div>
        </div>
      )}
      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
            <h2 className="pa-value-card-title" style={{ ...styles.cardTitle, margin: 0 }}><SlidersHorizontal size={18}/> Scenario assumptions</h2>
            <button className="pa-value-refresh-button" style={{ ...styles.secondaryButton, padding: '6px 10px', fontSize: 12 }} onClick={refresh}>Refresh data</button>
          </div>

          <label className="pa-value-label" style={styles.label}>Scenario</label>
          <select className="pa-value-input" value={scenarioId} onChange={(e) => setScenarioId(e.target.value)} style={styles.input}>
            {SCENARIOS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div className="pa-value-help-text" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{scenario.description}</div>

          <div style={styles.row}>
            <div>
              <label className="pa-value-label" style={styles.label}>Improvement target %</label>
              <input className="pa-value-input" type="number" value={targetReduction} min="0" max="100" onChange={(e) => setTargetReduction(e.target.value)} style={styles.input} />
            </div>
            <div>
              <label className="pa-value-label" style={styles.label}>Hourly cost</label>
              <input className="pa-value-input" type="number" value={hourlyCost} min="0" onChange={(e) => setHourlyCost(e.target.value)} style={styles.input} />
            </div>
          </div>

          <div style={styles.row}>
            <div>
              <label className="pa-value-label" style={styles.label}>Currency</label>
              <select className="pa-value-input" value={currency} onChange={(e) => setCurrency(e.target.value)} style={styles.input}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="pa-value-label" style={styles.label}>Delay value mode</label>
              <select className="pa-value-input" value={valueMode} onChange={(e) => setValueMode(e.target.value)} style={styles.input}>
                {Object.entries(VALUE_MODES).map(([id, m]) => <option key={id} value={id}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div className="pa-value-help-text" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{VALUE_MODES[valueMode]?.help}</div>

          <div style={styles.row}>
            <div>
              <label className="pa-value-label" style={styles.label}>Minutes per avoided handoff</label>
              <input className="pa-value-input" type="number" value={minutesPerHandoff} min="1" onChange={(e) => setMinutesPerHandoff(e.target.value)} style={styles.input} />
            </div>
            <div>
              <label className="pa-value-label" style={styles.label}>Minutes per avoided rework</label>
              <input className="pa-value-input" type="number" value={minutesPerRework} min="1" onChange={(e) => setMinutesPerRework(e.target.value)} style={styles.input} />
            </div>
          </div>

          <div style={styles.row}>
            <div>
              <label className="pa-value-label" style={styles.label}>Minutes per simplified long-tail variant item</label>
              <input className="pa-value-input" type="number" value={minutesPerVariant} min="1" onChange={(e) => setMinutesPerVariant(e.target.value)} style={styles.input} />
            </div>
            <div>
              <label className="pa-value-label" style={styles.label}>Minutes per automated transition</label>
              <input className="pa-value-input" type="number" value={minutesPerAutomatedTransition} min="1" onChange={(e) => setMinutesPerAutomatedTransition(e.target.value)} style={styles.input} />
            </div>
          </div>

          <div className="pa-value-help-text" style={{ fontSize: 12, color: C.muted, marginTop: 12, lineHeight: 1.45 }}>
            Use the top-right actions to <strong>Save Value Scenario</strong> for comparison or <strong>Save Finding</strong> to send the value opportunity to the Assessment.
          </div>
          {savedFindingMessage && <div style={styles.successNote}><CheckCircle2 size={15}/><span>{savedFindingMessage}</span></div>}
          {savedScenarioMessage && <div style={styles.successNote}><CheckCircle2 size={15}/><span>{savedScenarioMessage}</span></div>}

          <div style={styles.note}>
            <AlertTriangle size={16} />
            <div>Elapsed time reduction is converted into value using the selected value mode. Direct effort scenarios use minutes-per-event assumptions. This keeps the simulation defensible until richer SLA/cost data is available.</div>
          </div>
        </div>

        <div>
          <div style={styles.metricGrid}>
            <div style={styles.metric}><div style={styles.metricLabel}>Estimated hours saved</div><div style={styles.metricValue}>{fmtHours(result.hours)}</div></div>
            <div style={styles.metric}><div style={styles.metricLabel}>Estimated value</div><div style={styles.metricValue}>{fmtMoney(result.analysisSaving, result.currency)}</div></div>
            <div style={styles.metric}><div style={styles.metricLabel}>Annualised estimate</div><div style={styles.metricValue}>{fmtMoney(result.annualSaving, result.currency)}</div></div>
            <div style={styles.metric}><div style={styles.metricLabel}>Confidence</div><div style={styles.metricValue}><Badge tone={result.confidence === 'Low' ? 'orange' : 'blue'}>{result.confidence}</Badge></div></div>
          </div>

          <div style={styles.card}>
            <h2 style={styles.cardTitle}><Calculator size={18}/> Simulation result</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 12px' }}>
              <Badge>{result.scenarioLabel}</Badge>
              <Badge tone="green">{result.targetReduction}% target</Badge>
              <Badge tone="grey">{result.basis}</Badge>
            </div>
            <p style={{ color: C.body, fontSize: 13, lineHeight: 1.45 }}>
              This scenario estimates <strong>{fmtHours(result.hours)}</strong> of value-equivalent effort or delay reduction, worth approximately <strong>{fmtMoney(result.analysisSaving, result.currency)}</strong> for the analysis period. Annualised estimate: <strong>{fmtMoney(result.annualSaving, result.currency)}</strong>.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={styles.label}>Evidence basis</div>
                <ul style={{ margin: '6px 0 0 18px', padding: 0, color: C.body, fontSize: 13, lineHeight: 1.55 }}>
                  {result.evidence.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
              <div>
                <div style={styles.label}>Loaded process baseline</div>
                <ul style={{ margin: '6px 0 0 18px', padding: 0, color: C.body, fontSize: 13, lineHeight: 1.55 }}>
                  <li>{fmtNumber(baseline.totalTasks)} {entityLabel.toLowerCase()}s</li>
                  <li>{fmtNumber(baseline.variantCount)} variants</li>
                  <li>{fmtNumber(baseline.totalTransitions)} transitions</li>
                  <li>{fmtHours((baseline.avgDuration || 0) / 3600)} average elapsed duration</li>
                </ul>
              </div>
            </div>
            {result.notes.length > 0 && <div className="pa-value-note" style={styles.note}><AlertTriangle size={16}/><div>{result.notes.join(' ')}</div></div>}
          </div>

          <div style={{ ...styles.card, marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <h2 style={styles.cardTitle}><DollarSign size={18}/> Saved value scenarios</h2>
            </div>
            {saved.length === 0 ? (
              <div style={{ padding: 18, color: C.muted, fontSize: 13 }}>No saved value scenarios yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>Scenario</th><th style={styles.th}>Target</th><th style={styles.th}>Hours</th><th style={styles.th}>Value</th><th style={styles.th}>Annualised</th><th style={styles.th}></th></tr>
                </thead>
                <tbody>
                  {saved.map(s => (
                    <tr key={s.id}>
                      <td style={styles.td}><strong>{s.result?.scenarioLabel}</strong><br/><span style={{ color: C.muted, fontSize: 12 }}>{new Date(s.createdAt).toLocaleString()}</span></td>
                      <td style={styles.td}>{s.result?.targetReduction}%</td>
                      <td style={styles.td}>{fmtHours(s.result?.hours)}</td>
                      <td style={styles.td}>{fmtMoney(s.result?.analysisSaving, s.result?.currency || currency)}</td>
                      <td style={styles.td}>{fmtMoney(s.result?.annualSaving, s.result?.currency || currency)}</td>
                      <td style={styles.td}><button onClick={() => deleteScenario(s.id)} style={{ ...styles.secondaryButton, padding: 6 }} title="Delete"><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
